import type { FC } from 'react';
import { memo, useMemo } from 'react';
import type { GridColDef } from '@mui/x-data-grid';
import HypothesisSectionGroup from './HypothesisSectionGroup';
import HypothesisGroupCard from './HypothesisGroupCard';
import type {
  CreativeDnDMovePayload,
  HypothesisCreativeEntry,
  HypothesisGroup,
  HypothesisSection,
} from '../../types/hypothesis';
import {
  NO_HYPOTHESIS_KEY,
  type HypothesisPack,
} from '../../hooks/useCreativesByPack';
import useGroupSelectionSlices from '../../hooks/useGroupSelectionSlices';

const EMPTY_PACKS: HypothesisPack[] = [];

interface CreativesGroupedViewProps {
  thisWeek: HypothesisGroup[];
  upcoming: HypothesisGroup[];
  noHypothesis: HypothesisCreativeEntry[];
  packsByHypothesis: Map<string, HypothesisPack[]>;
  selectedCreatives: string[];
  columns: GridColDef[];
  dragEnabled?: boolean;
  canEditPackTag?: boolean;
  packTagsByIndex?: string[];
  onSelectionChange: (hypothesis: string, ids: string[]) => void;
  onPackSelectionChange: (packIndex: number, packCreativeIds: string[]) => void;
  onSectionChange: (hypothesis: string, section: HypothesisSection) => void;
  onCreativeMove?: (hypothesis: string, move: CreativeDnDMovePayload) => void;
}

const CreativesGroupedViewImpl: FC<CreativesGroupedViewProps> = ({
  thisWeek,
  upcoming,
  noHypothesis,
  packsByHypothesis,
  selectedCreatives,
  columns,
  dragEnabled = false,
  canEditPackTag = false,
  packTagsByIndex,
  onSelectionChange,
  onPackSelectionChange,
  onSectionChange,
  onCreativeMove,
}) => {
  const noHypothesisPacks =
    packsByHypothesis.get(NO_HYPOTHESIS_KEY) ?? EMPTY_PACKS;

  const noHypothesisSection = useMemo((): HypothesisSection | null => {
    const byId = new Map<string, HypothesisCreativeEntry>();
    for (const c of noHypothesis) {
      byId.set(c.id, c);
    }
    for (const pack of noHypothesisPacks) {
      for (const c of pack.creatives) {
        if (!byId.has(c.id)) byId.set(c.id, c);
      }
    }
    const merged = [...byId.values()];
    if (merged.length === 0) return null;
    if (merged.some((item) => item.section === 'this_week')) return 'this_week';
    if (merged.some((item) => item.section === 'upcoming')) return 'upcoming';
    return null;
  }, [noHypothesis, noHypothesisPacks]);

  const noHypothesisSliceGroups = useMemo(
    () => [{ hypothesis: NO_HYPOTHESIS_KEY, creatives: noHypothesis }],
    [noHypothesis],
  );
  const noHypothesisSlices = useGroupSelectionSlices(
    noHypothesisSliceGroups,
    selectedCreatives,
  );
  const noHypothesisSelectedIds =
    noHypothesisSlices.get(NO_HYPOTHESIS_KEY) ?? [];

  return (
    <>
      <HypothesisSectionGroup
        title="This week"
        groups={thisWeek}
        packsByHypothesis={packsByHypothesis}
        selectedCreatives={selectedCreatives}
        columns={columns}
        dragEnabled={dragEnabled}
        canEditPackTag={canEditPackTag}
        packTagsByIndex={packTagsByIndex}
        onSelectionChange={onSelectionChange}
        onPackSelectionChange={onPackSelectionChange}
        onSectionChange={onSectionChange}
        onCreativeMove={onCreativeMove}
      />
      <HypothesisSectionGroup
        title="Upcoming"
        groups={upcoming}
        packsByHypothesis={packsByHypothesis}
        selectedCreatives={selectedCreatives}
        columns={columns}
        dragEnabled={dragEnabled}
        canEditPackTag={canEditPackTag}
        packTagsByIndex={packTagsByIndex}
        onSelectionChange={onSelectionChange}
        onPackSelectionChange={onPackSelectionChange}
        onSectionChange={onSectionChange}
        onCreativeMove={onCreativeMove}
      />
      {(noHypothesis.length > 0 || noHypothesisPacks.length > 0) && (
        <HypothesisGroupCard
          hypothesis={NO_HYPOTHESIS_KEY}
          creatives={noHypothesis}
          packs={noHypothesisPacks}
          section={noHypothesisSection}
          showSectionSelector
          selectedIds={noHypothesisSelectedIds}
          columns={columns}
          dragEnabled={dragEnabled}
          canEditPackTag={canEditPackTag}
          packTagsByIndex={packTagsByIndex}
          onSelectionChange={onSelectionChange}
          onPackSelectionChange={onPackSelectionChange}
          onSectionChange={onSectionChange}
          onCreativeMove={onCreativeMove}
        />
      )}
    </>
  );
};

const CreativesGroupedView = memo(CreativesGroupedViewImpl);

export default CreativesGroupedView;
